import type { NavigationState, PartialState } from '@react-navigation/native';

type NavState = NavigationState | PartialState<NavigationState> | undefined;

/**
 * Deepest active leaf route name (e.g. "DataBrowser").
 */
export function getActiveRouteName(state: NavState): string | null {
  if (!state || !('routes' in state) || !state.routes?.length) {
    return null;
  }
  const index = typeof state.index === 'number' ? state.index : state.routes.length - 1;
  const route = state.routes[index];
  if (!route) return null;
  if (route.state) {
    return getActiveRouteName(route.state);
  }
  return route.name ?? null;
}

export type ActiveRouteCrumb = {
  name: string;
  key: string;
};

/**
 * Nested active route crumbs for breadcrumb UI.
 */
export function getActiveRouteCrumbs(state: NavState): ActiveRouteCrumb[] {
  const crumbs: ActiveRouteCrumb[] = [];
  let current: NavState = state;

  while (current && 'routes' in current && current.routes?.length) {
    const index = typeof current.index === 'number' ? current.index : current.routes.length - 1;
    const route = current.routes[index];
    if (!route?.name) break;
    crumbs.push({
      name: route.name,
      key: route.key ?? `${route.name}-${crumbs.length}`,
    });
    current = route.state;
  }

  return crumbs;
}

/**
 * Nested path of active routes (e.g. "Dashboard/Data/DataBrowser").
 */
export function getActiveRoutePath(state: NavState): string {
  return getActiveRouteCrumbs(state).map((crumb) => crumb.name).join('/');
}
